/* ==========================================================================
   MX Cart – mx-cart.js
   <mx-cart>: enhances the server-rendered cart page. Without this file the page still works as Shopify's cart form.

   Cart changes go through Shopify's Cart AJAX API with this section re-rendered in the same response (Section
   Rendering), one request at a time:
     quantity   /cart/change.js  { id: <line key>, quantity }      (line keys keep lines of the same variant apart,
                                                                    with their properties and subscriptions)
     remove     /cart/change.js  { id: <line key>, quantity: 0 }
     clear      /cart/clear.js
     discounts  /cart/update.js  { discount: 'CODE1,CODE2' }       (Shopify validates; a code is "applied" only when
                                                                    Shopify lists it on the cart afterwards)
     note       /cart/update.js  { note }
     add        /cart/add.js     { items: [{ id, quantity }] }     (gift wrap, recommendations)
   After each change the header is told: MXHeader.cart.refresh() when the MX Header is installed, else the
   mx:cart:refresh event. Changes made elsewhere (the cart drawer) reach this page through mx:cart:updated.
   Event on document: mx:cart-page:changed  detail: { itemCount }
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('mx-cart')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function qs(sel, ctx) { return ctx.querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call(ctx.querySelectorAll(sel)); }
  function readJSON(el) { try { return JSON.parse(el.textContent); } catch (e) { return null; } }
  function fill(tpl, map) { return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; }); }
  function cssEscape(v) { return window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&'); }
  /* the bottom of the theme's sticky or fixed header, wherever it is */
  function headerBottom() {
    var el = document.elementFromPoint(window.innerWidth / 2, 2);
    for (; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
      var pos = getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') { var r = el.getBoundingClientRect(); if (r.top <= 4) return Math.max(0, r.bottom); }
    }
    return 0;
  }

  class MXCart extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.cleanup = [];
      this.timers = {};
      /* a fresh start, also when the element is moved and connected again */
      this.dead = false; this.chooserBound = false; this.inFlight = 0; this.busy = false;
      this.cfg = readJSON(qs('[data-mxcart-cfg]', this)) || { labels: {} };
      this.labels = this.cfg.labels || {};
      this.sectionId = this.dataset.sectionId;
      this.chain = Promise.resolve();
      this.seq = 0;
      this.pending = {};
      this.ignoreExternal = 0;
      this.codes = this.readCodes(this);
      this.cartCodes = null;
      if (qs('[data-mxcart-discount]', this)) this.loadCodes();
      this.listen(this, 'click', function (e) { self.onClick(e); });
      this.listen(this, 'change', function (e) { self.onChange(e); });
      this.listen(this, 'input', function (e) { self.onInput(e); });
      this.listen(this, 'keydown', function (e) { self.onKey(e); });
      this.listen(this, 'submit', function (e) { self.onSubmit(e); });
      /* changes made elsewhere (the MX Header's cart drawer): show them here too */
      this.listen(document, 'mx:cart:updated', function () { if (!self.ignoreExternal && !self.busy) self.refresh(); });
      /* back/forward cache: the cart may have changed in another tab */
      this.listen(window, 'pageshow', function (e) { if (e.persisted) self.refresh(); });
      this.measureHeader();
      this.listen(window, 'resize', function () { self.timer('measure', function () { self.measureHeader(); }, 150); });
      if (this.classList.contains('mxcart-anim')) requestAnimationFrame(function () { requestAnimationFrame(function () { self.classList.add('is-in'); }); });
      this.initRecs();
    }
    disconnectedCallback() {
      var self = this;
      (this.cleanup || []).forEach(function (fn) { fn(); });
      Object.keys(this.timers || {}).forEach(function (k) { clearTimeout(self.timers[k]); clearInterval(self.timers[k]); });
      Object.keys(this.pending || {}).forEach(function (k) { clearTimeout(self.pending[k].timer); });
      this.cleanup = []; this.timers = {}; this.pending = {};
      this.dead = true;
    }
    listen(target, type, fn, opts) { target.addEventListener(type, fn, opts); this.cleanup.push(function () { target.removeEventListener(type, fn, opts); }); }
    timer(name, fn, ms) { clearTimeout(this.timers[name]); this.timers[name] = setTimeout(fn, ms); }
    announce(text) {
      var live = qs('[data-mxcart-live]', this);
      if (!live || !text) return;
      live.textContent = '';
      this.timer('live', function () { live.textContent = text; }, 60);
    }
    measureHeader() { this.style.setProperty('--mxcart-header-h', headerBottom() + 'px'); }
    readCodes(root) { return qsa('[data-mxcart-codes] li[data-code]', root).map(function (li) { return li.getAttribute('data-code'); }); }

    /* ============================================================ events */
    onClick(e) {
      var t = e.target.closest('button, a');
      if (!t || !this.contains(t)) return;
      if (t.hasAttribute('data-mxcart-step')) { e.preventDefault(); this.step(t); }
      else if (t.hasAttribute('data-mxcart-remove')) { e.preventDefault(); this.remove(t.closest('[data-mxcart-line]')); }
      else if (t.hasAttribute('data-mxcart-clear-open')) { this.toggleClear(true); }
      else if (t.hasAttribute('data-mxcart-clear-no')) { this.toggleClear(false); }
      else if (t.hasAttribute('data-mxcart-clear-yes')) { this.clear(); }
      else if (t.hasAttribute('data-mxcart-code-remove')) { this.removeCode(t.getAttribute('data-code')); }
      else if (t.hasAttribute('data-mxcart-share-copy')) { this.copyShare(t); }
      else if (t.hasAttribute('data-mxcart-info')) { var box = document.getElementById(t.getAttribute('aria-controls')); var open = t.getAttribute('aria-expanded') !== 'true'; t.setAttribute('aria-expanded', String(open)); if (box) box.hidden = !open; }
      else if (t.hasAttribute('data-mxcart-rec-choose')) { e.preventDefault(); this.openChooser(t); }
      else if (t.hasAttribute('data-mxcart-choose-close')) { this.closeChooser(); }
      else if (t.hasAttribute('data-mxcart-recs-prev')) { this.scrollRecs(-1); }
      else if (t.hasAttribute('data-mxcart-recs-next')) { this.scrollRecs(1); }
      else if (t.hasAttribute('data-mxcart-dot')) { this.goRecsPage(Number(t.getAttribute('data-mxcart-dot'))); }
      else if (t.hasAttribute('data-mxcart-pause')) { this.togglePause(t); }
      else if (t.hasAttribute('data-mxcart-checkout') && this.busy) { e.preventDefault(); }
    }
    onChange(e) {
      var t = e.target;
      if (t.matches('[data-mxcart-qty-input]')) this.typed(t);
      else if (t.matches('[data-mxcart-gift]')) this.gift(t);
      else if (t.matches('[data-mxcart-choose-option]')) this.chooserUpdate();
    }
    onInput(e) {
      var self = this;
      if (e.target.matches('[data-mxcart-note]')) {
        var field = e.target;
        this.timer('note', function () { self.saveNote(field.value); }, 700);
      } else if (e.target.matches('[data-mxcart-code]')) {
        e.target.removeAttribute('aria-invalid');
      }
    }
    onKey(e) {
      if (e.key === 'Enter' && e.target.matches('[data-mxcart-qty-input]')) { e.preventDefault(); this.typed(e.target); }
      if (e.key === ' ' && e.target.matches('[data-mxcart-rec-choose]')) { e.preventDefault(); this.openChooser(e.target); }
      if (e.key === 'Escape' && qs('[data-mxcart-clear-yes]', this) && !qs('.mxcart-clear__confirm', this).hidden) { this.toggleClear(false); }
    }
    onSubmit(e) {
      var form = e.target;
      if (form.matches('[data-mxcart-discount-form]')) { e.preventDefault(); this.applyCode(); return; }
      if (form.matches('[data-mxcart-rec-form]')) { e.preventDefault(); this.addRec(form); return; }
      if (form.matches('[data-mxcart-choose-form]')) { e.preventDefault(); this.chooserAdd(); return; }
      if (form.matches('[data-mxcart-form]')) {
        var sub = e.submitter || document.activeElement;
        var checkout = !!sub && sub.name === 'checkout';
        /* checkout goes to Shopify as a normal form post (with the note), but never in the middle of a change */
        if (!checkout || this.busy) e.preventDefault();
        if (checkout && !this.busy) { var note = qs('[data-mxcart-note]', this); if (note) clearTimeout(this.timers.note); }
      }
    }

    /* ============================================================ quantity, removal, clearing */
    limits(line) {
      var q = qs('[data-mxcart-qty]', line);
      return { min: Number(q.getAttribute('data-min')) || 1, step: Number(q.getAttribute('data-step')) || 1, max: q.hasAttribute('data-max') ? Number(q.getAttribute('data-max')) : null };
    }
    step(btn) {
      var line = btn.closest('[data-mxcart-line]'), input = qs('[data-mxcart-qty-input]', line), l = this.limits(line);
      var n = (parseInt(input.value, 10) || 0) + Number(btn.getAttribute('data-mxcart-step')) * l.step;
      n = Math.max(l.min, n);
      if (l.max != null) n = Math.min(l.max, n);
      input.value = n;
      this.syncStepButtons(line, n);
      this.queueQuantity(line, n, 380);
    }
    typed(input) {
      var line = input.closest('[data-mxcart-line]'), l = this.limits(line);
      var n = parseInt(input.value, 10);
      if (isNaN(n) || n < 0) n = Number(line.getAttribute('data-quantity'));
      if (n === 0) { this.remove(line); return; }
      n = Math.max(l.min, n);
      if (l.max != null && n > l.max) n = l.max;
      var off = (n - l.min) % l.step;
      if (off) n -= off;
      input.value = n;
      this.syncStepButtons(line, n);
      this.queueQuantity(line, n, 0);
    }
    syncStepButtons(line, n) {
      var l = this.limits(line);
      var minus = qs('[data-mxcart-step="-1"]', line), plus = qs('[data-mxcart-step="1"]', line);
      if (minus) minus.disabled = n <= l.min;
      if (plus) plus.disabled = l.max != null && n >= l.max;
    }
    /* clicks on + / − are gathered for a moment, then one request is sent for the final quantity */
    queueQuantity(line, qty, wait) {
      var self = this, key = line.getAttribute('data-key');
      var p = this.pending[key] || (this.pending[key] = {});
      clearTimeout(p.timer);
      p.qty = qty;
      line.classList.add('is-updating');
      /* checkout waits for the change the shopper is making */
      this.setBusy(true);
      p.timer = setTimeout(function () {
        delete self.pending[key];
        if (Number(line.getAttribute('data-quantity')) === qty) { line.classList.remove('is-updating'); if (!self.inFlight && !Object.keys(self.pending).length) self.setBusy(false); return; }
        self.mutate(self.dataset.changeUrl, { id: key, quantity: qty }, { key: key });
      }, wait);
    }
    remove(line) {
      if (!line) return;
      var key = line.getAttribute('data-key');
      if (this.pending[key]) { clearTimeout(this.pending[key].timer); delete this.pending[key]; }
      line.classList.add('is-removing');
      this.mutate(this.dataset.changeUrl, { id: key, quantity: 0 }, { key: key, removing: true });
    }
    toggleClear(open) {
      var box = qs('.mxcart-clear__confirm', this), btn = qs('[data-mxcart-clear-open]', this);
      if (!box) return;
      box.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      (open ? qs('[data-mxcart-clear-no]', this) : btn).focus();
    }
    clear() { this.mutate(this.dataset.clearUrl, {}, { clear: true }); }

    /* ============================================================ requests */
    setBusy(on) {
      this.busy = on;
      this.classList.toggle('is-busy', on);
      var co = qs('[data-mxcart-checkout]', this);
      if (co) co.disabled = on;
      var list = qs('[data-mxcart-region="lines"]', this);
      if (list) list.setAttribute('aria-busy', String(on));
    }
    /* one request at a time; only the newest result is drawn (older ones would only flash past) */
    mutate(url, body, opts) {
      var self = this;
      opts = opts || {};
      var seq = ++this.seq;
      this.inFlight = (this.inFlight || 0) + 1;
      this.setBusy(true);
      this.announce(this.labels.updating);
      body.sections = [this.sectionId];
      body.sections_url = window.location.pathname;
      var job = function () {
        if (self.dead) return null;
        return fetch(url.replace(/\.js$/, '') + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) })
          .then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (data) {
              if (!res.ok || (data && data.status && Number(data.status) >= 400)) {
                var err = new Error((data && (data.description || data.message)) || '');
                err.status = res.status; err.known = res.status === 422 || res.status === 404;
                throw err;
              }
              return data;
            });
          })
          .then(function (data) {
            if (self.dead) return data;
            if (Array.isArray(data.discount_codes)) self.cartCodes = data.discount_codes;
            /* the newest result is drawn; discount checks always draw theirs, so the verdict matches the page */
            if ((seq === self.seq || opts.always) && data.sections && data.sections[self.sectionId]) self.apply(data.sections[self.sectionId], opts);
            else self.renderCodes();
            self.afterChange(data);
            if (seq === self.seq) self.announce(opts.message || self.labels.updated);
            return data;
          }, function (err) {
            if (self.dead) throw err;
            /* draw the cart as it really is, then explain on the line (or above the summary) */
            return self.refresh({ reset: true }).then(function () {
              var msg = (err.known && err.message) || self.labels.error;
              var line = opts.key && qs('[data-mxcart-line][data-key="' + cssEscape(opts.key) + '"]', self);
              var box = line && qs('[data-mxcart-line-error]', line);
              if (box) { box.textContent = msg; box.hidden = false; }
              else if (opts.onError) opts.onError(msg);
              else self.announce(msg);
              if (!box) self.announce(msg);
              throw err;
            });
          })
          .finally(function () { self.inFlight--; if (seq === self.seq && !Object.keys(self.pending).length) self.setBusy(false); });
      };
      var p = this.chain.then(job, job);
      this.chain = p.catch(function () { /* the chain carries on */ });
      return p;
    }
    refresh(opts) {
      var self = this;
      opts = opts || {};
      return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId), { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) { if (!self.dead) self.apply(html, opts); })
        .catch(function () { /* keep what is shown */ });
    }
    afterChange(data) {
      var self = this;
      var count = data && typeof data.item_count === 'number' ? data.item_count : Number((qs('[data-mxcart-body]', this) || {}).getAttribute && qs('[data-mxcart-body]', this).getAttribute('data-count'));
      document.dispatchEvent(new CustomEvent('mx:cart-page:changed', { detail: { itemCount: count } }));
      var mx = window.MXHeader && window.MXHeader.cart;
      if (mx && typeof mx.refresh === 'function') {
        /* the header's own refresh announces mx:cart:updated, which must not bounce back here */
        this.ignoreExternal++;
        Promise.resolve(mx.refresh()).catch(function () {}).then(function () { setTimeout(function () { self.ignoreExternal--; }, 50); });
      } else {
        this.ignoreExternal++;
        document.dispatchEvent(new CustomEvent('mx:cart:refresh', { detail: { source: 'mx-cart-page' } }));
        setTimeout(function () { self.ignoreExternal--; }, 50);
      }
      this.filterRecs();
    }

    /* ============================================================ drawing a new render */
    focusInfo() {
      var a = document.activeElement;
      if (!a || !this.contains(a)) return null;
      var line = a.closest('[data-mxcart-line]');
      return { key: line && line.getAttribute('data-key'), index: line ? qsa('[data-mxcart-line]', this).indexOf(line) : -1, focus: a.getAttribute('data-focus'), region: a.closest('[data-mxcart-region]') && a.closest('[data-mxcart-region]').getAttribute('data-mxcart-region') };
    }
    apply(html, opts) {
      opts = opts || {};
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = qs('[data-mxcart-body]', doc), body = qs('[data-mxcart-body]', this);
      if (!fresh || !body) return;
      var f = this.focusInfo();
      var wasEmpty = body.getAttribute('data-count') === '0', isEmpty = fresh.getAttribute('data-count') === '0';
      if (wasEmpty !== isEmpty) {
        /* cart emptied (or filled): the whole page changes; focus goes to the new heading if it was on this page */
        var dialogOpen = qs('[data-mxcart-choose]', this) && qs('[data-mxcart-choose]', this).open;
        body.innerHTML = fresh.innerHTML;
        body.setAttribute('data-count', fresh.getAttribute('data-count'));
        var h = qs('[data-mxcart-heading]', body);
        if (h && (f || dialogOpen) && !dialogOpen) h.focus({ preventScroll: false });
        this.codes = this.readCodes(this);
        this.renderCodes();
        this.initRecs();
        return;
      }
      /* quantities the shopper is still changing (queued, or typed and not sent) survive a new render */
      var keep = {}, self = this;
      qsa('[data-mxcart-line]', body).forEach(function (line) {
        var key = line.getAttribute('data-key'), input = qs('[data-mxcart-qty-input]', line);
        if (self.pending[key]) keep[key] = { value: String(self.pending[key].qty), pending: true };
        else if (!opts.reset && input && input === document.activeElement && input.value !== line.getAttribute('data-quantity')) keep[key] = { value: input.value, pending: false };
      });
      body.setAttribute('data-count', fresh.getAttribute('data-count'));
      qsa('[data-mxcart-region]', fresh).forEach(function (region) {
        var name = region.getAttribute('data-mxcart-region');
        var mine = qs('[data-mxcart-region="' + name + '"]', body);
        if (mine && mine.innerHTML !== region.innerHTML) mine.innerHTML = region.innerHTML;
      });
      Object.keys(keep).forEach(function (key) {
        var line = qs('[data-mxcart-line][data-key="' + cssEscape(key) + '"]', body);
        var input = line && qs('[data-mxcart-qty-input]', line);
        if (!input) return;
        input.value = keep[key].value;
        if (keep[key].pending) { line.classList.add('is-updating'); self.syncStepButtons(line, Number(keep[key].value)); }
      });
      this.codes = this.readCodes(this);
      this.renderCodes();
      if (f) this.restoreFocus(f);
    }
    restoreFocus(f) {
      var el = null;
      if (f.key) {
        var line = qs('[data-mxcart-line][data-key="' + cssEscape(f.key) + '"]', this);
        if (line) el = f.focus && qs('[data-focus="' + f.focus + '"]', line);
        else {
          /* the line is gone: the same control on the line that took its place, else the heading */
          var lines = qsa('[data-mxcart-line]', this);
          var next = lines[Math.min(f.index, lines.length - 1)];
          el = next ? qs('[data-focus="remove"]', next) : qs('[data-mxcart-heading]', this);
        }
      } else if (f.focus) {
        el = qs('[data-focus="' + f.focus + '"]', this);
      }
      if (!el && f.region === 'codes') el = qs('[data-mxcart-code]', this);
      if (el && el !== document.activeElement) {
        el.focus({ preventScroll: true });
        if (el.select && el.matches('[data-mxcart-qty-input]')) el.select();
      }
    }

    /* ============================================================ discount codes */
    msg(text, ok) {
      var box = qs('[data-mxcart-discount-msg]', this);
      if (!box) return;
      box.textContent = text || '';
      box.classList.toggle('is-ok', !!ok);
      box.classList.toggle('is-error', !ok && !!text);
    }
    /* every code on the cart with Shopify's verdict (cart JSON discount_codes); the page only lists applied ones */
    loadCodes() {
      var self = this;
      fetch((this.dataset.cartUrl || '/cart').replace(/\/$/, '') + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (!self.dead && c && Array.isArray(c.discount_codes)) { self.cartCodes = c.discount_codes; self.renderCodes(); } })
        .catch(function () { /* keep the server-rendered list */ });
    }
    allCodes() { return this.cartCodes ? this.cartCodes.map(function (d) { return d.code; }) : this.codes.slice(); }
    renderCodes() {
      var region = qs('[data-mxcart-region="codes"]', this);
      if (!region || !this.cartCodes) return;
      var labels = this.labels, list = document.createElement('ul');
      list.className = 'mxcart-codes'; list.setAttribute('role', 'list'); list.setAttribute('data-mxcart-codes', '');
      var tpl = qs('[data-mxcart-code-tpl]', this), svgs = tpl ? qsa('svg', tpl.content) : [];
      var icon = svgs[0] ? svgs[0].outerHTML : '', close = svgs[1] ? svgs[1].outerHTML : '';
      var removeTpl = region.getAttribute('data-remove-label') || 'Remove discount [code]';
      this.cartCodes.forEach(function (d) {
        var li = document.createElement('li');
        li.className = 'mxcart-code' + (d.applicable ? '' : ' mxcart-code--off');
        li.setAttribute('data-code', d.code);
        var name = document.createElement('span'); name.textContent = d.applicable ? d.code : d.code + ' (' + (labels.notApplicable || 'not applicable') + ')';
        var btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'mxcart-code__remove'; btn.setAttribute('data-mxcart-code-remove', ''); btn.setAttribute('data-code', d.code);
        btn.setAttribute('data-focus', 'code-' + d.code.toLowerCase()); btn.setAttribute('aria-label', fill(removeTpl, { code: d.code })); btn.innerHTML = close;
        li.innerHTML = icon; li.appendChild(name); li.appendChild(btn);
        list.appendChild(li);
      });
      region.innerHTML = '';
      if (this.cartCodes.length) region.appendChild(list);
      this.codes = this.cartCodes.filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
    }
    applyCode() {
      var self = this;
      var input = qs('[data-mxcart-code]', this), btn = qs('.mxcart-discount__apply', this);
      var code = (input.value || '').trim();
      if (!code) { input.setAttribute('aria-invalid', 'true'); this.msg(this.labels.empty, false); input.focus(); return; }
      var before = this.allCodes();
      if (this.codes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })) { this.msg(fill(this.labels.applied, { code: code }), true); return; }
      before = before.filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
      if (code.indexOf(',') > -1) { this.msg(fill(this.labels.invalid, { code: code }), false); return; }
      btn.disabled = true; input.setAttribute('aria-busy', 'true');
      this.msg('', false);
      this.mutate(this.dataset.updateUrl, { discount: before.concat(code).join(',') }, { message: ' ', always: true }).then(function (data) {
        /* Shopify's verdict: discount_codes[].applicable when the cart JSON has it, else whether the cart lists the code */
        var flag = (data && data.discount_codes || []).filter(function (d) { return String(d.code).toLowerCase() === code.toLowerCase(); })[0];
        var listed = qsa('[data-mxcart-codes] li[data-code]', self).some(function (li) { return li.getAttribute('data-code').toLowerCase() === code.toLowerCase() && !li.classList.contains('mxcart-code--off'); });
        var applied = flag ? !!flag.applicable : listed;
        if (applied) {
          input.value = '';
          self.msg(fill(self.labels.applied, { code: code }), true);
          self.announce(fill(self.labels.applied, { code: code }));
        } else {
          self.msg(fill(self.labels.invalid, { code: code }), false);
          input.setAttribute('aria-invalid', 'true');
          self.announce(fill(self.labels.invalid, { code: code }));
          /* a code Shopify did not accept is not left on the cart; the codes that were there stay */
          return self.mutate(self.dataset.updateUrl, { discount: before.join(',') }, { message: ' ', always: true });
        }
      }, function () {
        self.msg(self.labels.error, false);
      }).then(function () { btn.disabled = false; input.removeAttribute('aria-busy'); }, function () { btn.disabled = false; input.removeAttribute('aria-busy'); });
    }
    removeCode(code) {
      var rest = this.allCodes().filter(function (c) { return c.toLowerCase() !== String(code).toLowerCase(); });
      this.msg('', false);
      this.mutate(this.dataset.updateUrl, { discount: rest.join(',') });
    }

    /* ============================================================ note, gift wrapping, share */
    saveNote(value) {
      var self = this, status = qs('[data-mxcart-note-status]', this);
      fetch(this.dataset.updateUrl.replace(/\.js$/, '') + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: value }) })
        .then(function (r) { if (!r.ok) throw new Error(); if (status) { status.textContent = self.labels.noteSaved; self.timer('noteStatus', function () { status.textContent = ''; }, 2500); } })
        .catch(function () { if (status) status.textContent = self.labels.error; });
    }
    gift(box) {
      box.disabled = true;
      var opts = { onError: function () {} };
      var done = function () { box.disabled = false; };
      var p = box.checked
        ? (box.hasAttribute('data-key') ? Promise.resolve() : this.mutate(this.dataset.addUrl, { items: [{ id: Number(box.getAttribute('data-variant-id')), quantity: 1 }] }, opts))
        : (box.hasAttribute('data-key') ? this.mutate(this.dataset.changeUrl, { id: box.getAttribute('data-key'), quantity: 0 }, opts) : Promise.resolve());
      p.then(done, done);
    }
    copyShare(btn) {
      var self = this, wrap = btn.closest('[data-mxcart-share]'), link = wrap.getAttribute('data-link');
      var status = qs('[data-mxcart-share-status]', wrap), field = qs('[data-mxcart-share-field]', wrap);
      var ok = function () { status.textContent = self.labels.copied; self.timer('share', function () { status.textContent = ''; }, 3000); };
      var fallback = function () { field.hidden = false; field.focus(); field.select(); status.textContent = self.labels.copyFallback; };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(link).then(ok, fallback);
      else fallback();
    }

    /* ============================================================ recommendations */
    initRecs() {
      var self = this;
      this.recs = qs('[data-mxcart-recs]', this);
      this.recList = qs('[data-mxcart-recs-list]', this);
      if (!this.recs || !this.recList) return;
      this.listen(this.recList, 'scroll', function () { self.timer('recsScroll', function () { self.updateRecsNav(); }, 60); }, { passive: true });
      this.updateRecsNav();
      this.startAutoplay();
      if (!this.cfg.recs) return;
      var ids = this.cartProductIds();
      if (!ids.length) return;
      var url = this.dataset.recsUrl, limit = this.cfg.limit;
      var lists = ids.slice(0, this.cfg.sources || 1).map(function (id) {
        return fetch(url + '?product_id=' + encodeURIComponent(id) + '&limit=' + Math.min(10, limit + ids.length) + '&intent=' + encodeURIComponent(self.cfg.intent) + '&section_id=' + encodeURIComponent(self.sectionId), { credentials: 'same-origin' })
          .then(function (r) { return r.text().then(function (t) { return r.ok ? t : ''; }); })
          .then(function (html) { var d = new DOMParser().parseFromString(html, 'text/html'); return qsa('[data-mxcart-recs-result] [data-mxcart-rec]', d); })
          .catch(function () { return []; });
      });
      Promise.all(lists).then(function (groups) {
        if (self.dead || !self.recList.isConnected) return;
        var seen = {}, inCart = self.cartProductIds(), picked = [];
        /* take the products in turn from each list, so every cart product contributes */
        for (var i = 0; picked.length < limit; i++) {
          var any = false;
          groups.forEach(function (g) {
            var card = g[i];
            if (!card) return;
            any = true;
            var id = card.getAttribute('data-product-id');
            if (picked.length >= limit || seen[id] || inCart.indexOf(id) > -1) return;
            seen[id] = true; picked.push(card);
          });
          if (!any) break;
        }
        if (!picked.length) return;
        self.recList.innerHTML = '';
        picked.forEach(function (c) { self.recList.appendChild(document.importNode(c, true)); });
        self.recs.hidden = false;
        self.updateRecsNav();
      });
    }
    cartProductIds() {
      var seen = {};
      return qsa('[data-mxcart-line]', this).map(function (l) { return l.getAttribute('data-product-id'); }).filter(function (id) { if (!id || seen[id]) return false; seen[id] = true; return true; });
    }
    /* a product just added to the cart leaves the list */
    filterRecs() {
      if (!this.recList || !this.recList.isConnected) return;
      var inCart = this.cartProductIds(), self = this;
      qsa('[data-mxcart-rec]', this.recList).forEach(function (c) {
        if (inCart.indexOf(c.getAttribute('data-product-id')) < 0) return;
        if (c.contains(document.activeElement)) {
          /* focus goes to the next card's button (or the previous, or the heading) before this card leaves */
          var next = c.nextElementSibling || c.previousElementSibling;
          var target = next ? qs('.mxcart-rec__btn', next) : qs('#' + cssEscape(self.recs.getAttribute('aria-labelledby')), self) || qs('[data-mxcart-heading]', self);
          if (target) target.focus({ preventScroll: true });
        }
        c.remove();
      });
      if (!qs('[data-mxcart-rec]', this.recList)) { if (this.recs.contains(document.activeElement)) { var h = qs('[data-mxcart-heading]', this); if (h) h.focus({ preventScroll: true }); } this.recs.hidden = true; }
      this.updateRecsNav();
    }
    addRec(form) {
      var self = this, btn = qs('[data-mxcart-rec-add]', form), id = Number(new FormData(form).get('id'));
      if (btn.getAttribute('aria-busy') === 'true') return;
      /* busy, but not disabled: a disabled button would drop the keyboard focus */
      btn.setAttribute('aria-busy', 'true'); btn.setAttribute('aria-disabled', 'true');
      var label = btn.textContent;
      this.mutate(this.dataset.addUrl, { items: [{ id: id, quantity: 1 }] }, { message: this.labels.added, onError: function (msg) { self.announce(msg); } })
        .then(function () { btn.textContent = self.labels.added; self.timer('recBtn', function () { btn.textContent = label; }, 2000); }, function () {})
        .then(function () { btn.removeAttribute('aria-busy'); btn.removeAttribute('aria-disabled'); });
    }
    /* products with options: choose the variant first (never an arbitrary one) */
    openChooser(trigger) {
      var dlg = qs('[data-mxcart-choose]', this);
      var data = readJSON(qs('[data-mxcart-rec-variants]', trigger.closest('[data-mxcart-rec]')));
      if (!dlg || !data || typeof dlg.showModal !== 'function') { window.location.href = trigger.href; return; }
      this.chooser = { data: data, trigger: trigger };
      qs('[data-mxcart-choose-title]', dlg).textContent = data.title;
      var first = data.variants.filter(function (v) { return v.available; })[0] || data.variants[0];
      var box = qs('[data-mxcart-choose-options]', dlg);
      box.innerHTML = '';
      data.options.forEach(function (name, i) {
        var values = [];
        data.variants.forEach(function (v) { if (values.indexOf(v.options[i]) < 0) values.push(v.options[i]); });
        var label = document.createElement('label'); label.className = 'mxcart-choose__field';
        var span = document.createElement('span'); span.textContent = name;
        var sel = document.createElement('select'); sel.setAttribute('data-mxcart-choose-option', String(i));
        values.forEach(function (val) { var o = document.createElement('option'); o.value = val; o.textContent = val; if (val === first.options[i]) o.selected = true; sel.appendChild(o); });
        label.appendChild(span); label.appendChild(sel); box.appendChild(label);
      });
      qs('[data-mxcart-choose-error]', dlg).hidden = true;
      this.chooserUpdate();
      dlg.showModal();
      var firstSel = qs('select', box);
      if (firstSel) firstSel.focus();
      var self = this;
      if (!this.chooserBound) {
        this.chooserBound = true;
        this.listen(dlg, 'close', function () {
          var t = self.chooser && self.chooser.trigger;
          if (t && document.contains(t)) t.focus();
          else { var alt = qs('[data-mxcart-recs] .mxcart-rec__btn', self) || qs('[data-mxcart-heading]', self); if (alt) alt.focus({ preventScroll: true }); }
        });
        this.listen(dlg, 'click', function (e) { if (e.target === dlg) self.closeChooser(); });
      }
    }
    chooserUpdate() {
      var dlg = qs('[data-mxcart-choose]', this), c = this.chooser;
      if (!c) return;
      var values = qsa('[data-mxcart-choose-option]', dlg).map(function (s) { return s.value; });
      var v = c.data.variants.filter(function (x) { return x.options.every(function (o, i) { return o === values[i]; }); })[0] || null;
      c.variant = v;
      var add = qs('[data-mxcart-choose-add]', dlg), price = qs('[data-mxcart-choose-price]', dlg);
      price.innerHTML = v ? v.price : '';
      add.disabled = !v || !v.available;
      add.textContent = v && v.available ? this.labels.add : this.labels.unavailable;
    }
    chooserAdd() {
      var self = this, dlg = qs('[data-mxcart-choose]', this), v = this.chooser && this.chooser.variant;
      if (!v || !v.available) return;
      var add = qs('[data-mxcart-choose-add]', dlg), err = qs('[data-mxcart-choose-error]', dlg);
      add.disabled = true;
      this.mutate(this.dataset.addUrl, { items: [{ id: v.id, quantity: 1 }] }, { message: this.labels.added, onError: function (msg) { err.textContent = msg; err.hidden = false; } })
        .then(function () { self.closeChooser(); }, function () {})
        .then(function () { add.disabled = false; });
    }
    closeChooser() { var dlg = qs('[data-mxcart-choose]', this); if (dlg && dlg.open) dlg.close(); }

    /* carousel: arrows, dots, optional autoplay (paused on hover and focus, never with reduced motion) */
    pageWidth() { return this.recList.clientWidth + 14; }
    scrollRecs(dir) {
      if (!this.recList) return;
      this.recList.scrollBy({ left: dir * this.pageWidth(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    goRecsPage(i) { this.recList.scrollTo({ left: i * this.pageWidth(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }); }
    updateRecsNav() {
      var list = this.recList;
      if (!list || !list.isConnected) return;
      var max = list.scrollWidth - list.clientWidth;
      var prev = qs('[data-mxcart-recs-prev]', this), next = qs('[data-mxcart-recs-next]', this);
      if (prev) prev.disabled = list.scrollLeft <= 2;
      if (next) next.disabled = list.scrollLeft >= max - 2;
      var dots = qs('[data-mxcart-recs-dots]', this);
      if (dots) {
        var pages = Math.max(1, Math.ceil((list.scrollWidth + 14) / this.pageWidth() - 0.01));
        var current = Math.round(list.scrollLeft / this.pageWidth());
        if (dots.children.length !== pages) {
          dots.innerHTML = '';
          for (var i = 0; i < pages; i++) { var b = document.createElement('button'); b.type = 'button'; b.className = 'mxcart-recs__dot'; b.setAttribute('data-mxcart-dot', String(i)); b.setAttribute('aria-label', 'Show products, page ' + (i + 1) + ' of ' + pages); dots.appendChild(b); }
        }
        qsa('.mxcart-recs__dot', dots).forEach(function (d, n) { if (n === current) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
        dots.hidden = pages < 2;
      }
    }
    togglePause(btn) {
      var on = btn.getAttribute('aria-pressed') !== 'true';
      btn.setAttribute('aria-pressed', String(on));
      btn.textContent = on ? (this.labels.play || 'Play') : (this.labels.pause || 'Pause');
      if (btn.getAttribute('data-mxcart-pause') === 'strip') btn.closest('.mxcart-strip').classList.toggle('is-paused', on);
      else this.autoplayStopped = on;
    }
    startAutoplay() {
      var self = this, ms = this.cfg.autoplay;
      clearInterval(this.timers.autoplay);
      if (!ms || reduceMotion.matches || !this.recs || this.classList.contains('mxcart--recs-grid')) return;
      var paused = false;
      var pause = function () { paused = true; }, resume = function () { paused = false; };
      this.listen(this.recs, 'mouseenter', pause); this.listen(this.recs, 'mouseleave', resume);
      this.listen(this.recs, 'focusin', pause); this.listen(this.recs, 'focusout', resume);
      this.timers.autoplay = setInterval(function () {
        if (paused || self.autoplayStopped || document.hidden || !self.recList.isConnected) return;
        var max = self.recList.scrollWidth - self.recList.clientWidth;
        if (self.recList.scrollLeft >= max - 2) self.goRecsPage(0); else self.scrollRecs(1);
      }, ms);
    }
  }

  customElements.define('mx-cart', MXCart);
})();
